"use client";

import * as React from "react";
import { twMerge } from "tailwind-merge";

/**
 * SectionNav — In-page navigation for content sections with smooth scrolling
 *
 * Provides sticky sidebar navigation on desktop and horizontal scrollable tabs on mobile.
 * Automatically tracks the active section based on scroll position and enables smooth
 * scrolling to sections when clicked.
 *
 * ## Features
 * - Responsive: Sidebar on desktop (lg+), horizontal tabs on mobile
 * - Sticky positioning for persistent visibility
 * - Smooth scroll to sections
 * - Active state management
 * - Customizable styling and positioning
 *
 * @example
 * ```tsx
 * <SectionNav
 *   sections={[
 *     { id: 'overview', label: 'Overview' },
 *     { id: 'features', label: 'Features' },
 *     { id: 'pricing', label: 'Pricing' }
 *   ]}
 *   activeSection="overview"
 *   onSectionChange={(id) => console.log('Active section:', id)}
 * />
 * ```
 */

export type SectionNavItem = {
  /** Unique identifier that matches the section's HTML id */
  id: string;
  /** Display label for the navigation item */
  label: string;
  /** Optional count badge to display next to the label */
  count?: number;
};

export type SectionNavOrientation = "vertical" | "horizontal" | "responsive";

export type SectionNavProps = {
  /** Array of section items to navigate between */
  sections: SectionNavItem[];
  /** Currently active section id */
  activeSection?: string | null;
  /** Callback when active section changes (controlled mode) */
  onSectionChange?: (sectionId: string) => void;
  /** Navigation orientation: vertical (sidebar), horizontal (tabs), or responsive (auto) */
  orientation?: SectionNavOrientation;
  /** Additional CSS classes for the nav container */
  className?: string;
  /** Custom scroll behavior options */
  scrollBehavior?: ScrollBehavior;
  /** Top offset for sticky positioning (default: 24px / 1.5rem) */
  stickyTop?: string;
  /** ID prefix for section elements (default: 'section-') */
  sectionIdPrefix?: string;
  /** Custom filter function to determine which sections are visible */
  filterSection?: (section: SectionNavItem) => boolean;
};

export function SectionNav({
  sections,
  activeSection: controlledActiveSection,
  onSectionChange,
  orientation = "responsive",
  className,
  scrollBehavior = "smooth",
  stickyTop = "1.5rem",
  sectionIdPrefix = "section-",
  filterSection,
}: SectionNavProps) {
  const verticalNavRef = React.useRef<HTMLElement>(null);
  const horizontalNavRef = React.useRef<HTMLElement>(null);
  const [internalActiveSection, setInternalActiveSection] = React.useState<string | null>(sections[0]?.id || null);

  // Use controlled or uncontrolled active section
  const activeSection = controlledActiveSection ?? internalActiveSection;

  // Track scroll position to update active section
  React.useEffect(() => {
    // If fully controlled (has both activeSection and onSectionChange), skip auto-tracking
    if (controlledActiveSection !== undefined && onSectionChange) {
      return;
    }

    // Check if a nav element is actually visible (not hidden by CSS)
    // This handles cases where multiple SectionNav instances are rendered but hidden with CSS
    const isNavVisible = (navEl: HTMLElement | null) => {
      if (!navEl) return false;
      const style = window.getComputedStyle(navEl);
      return style.display !== "none" && style.visibility !== "hidden" && style.opacity !== "0";
    };

    // Wait a tick to ensure DOM is ready and CSS is applied
    const checkAndObserve = () => {
      // Check which nav (vertical or horizontal) is currently visible
      const visibleVertical = isNavVisible(verticalNavRef.current);
      const visibleHorizontal = isNavVisible(horizontalNavRef.current);

      if (!visibleVertical && !visibleHorizontal) {
        return null;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const id = entry.target.id.replace(sectionIdPrefix, "");
              if (onSectionChange) {
                onSectionChange(id);
              } else {
                setInternalActiveSection(id);
              }
            }
          });
        },
        {
          rootMargin: "-20% 0px -70% 0px",
          threshold: 0,
        }
      );

      sections.forEach((section) => {
        const element = document.getElementById(`${sectionIdPrefix}${section.id}`);
        if (element) {
          observer.observe(element);
        }
      });

      return observer;
    };

    // Initial setup
    let observer = checkAndObserve();

    // Re-check visibility on window resize
    const handleResize = () => {
      if (observer) {
        observer.disconnect();
      }
      observer = checkAndObserve();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [sections, sectionIdPrefix, controlledActiveSection, onSectionChange, orientation]);

  // Filter sections if filter function provided
  const visibleSections = React.useMemo(() => {
    return filterSection ? sections.filter(filterSection) : sections;
  }, [sections, filterSection]);

  const handleSectionClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
      e.preventDefault(); // Prevent default hash navigation

      const element = document.getElementById(`${sectionIdPrefix}${sectionId}`);
      if (element) {
        element.scrollIntoView({ behavior: scrollBehavior, block: "start" });

        // Update URL hash without triggering scroll
        if (window.history.pushState) {
          window.history.pushState(null, "", `#${sectionIdPrefix}${sectionId}`);
        }

        // Update active section
        if (onSectionChange) {
          onSectionChange(sectionId);
        } else {
          setInternalActiveSection(sectionId);
        }
      }
    },
    [sectionIdPrefix, scrollBehavior, onSectionChange]
  );

  if (visibleSections.length === 0) {
    return null;
  }

  // Determine display classes based on orientation
  const verticalClasses =
    orientation === "vertical"
      ? "" // Always show
      : orientation === "horizontal"
        ? "hidden" // Never show
        : "hidden lg:block"; // Responsive: show on desktop

  const horizontalClasses =
    orientation === "horizontal"
      ? "" // Always show
      : orientation === "vertical"
        ? "hidden" // Never show
        : "lg:hidden"; // Responsive: show on mobile

  return (
    <>
      {/* Vertical Sidebar Navigation */}
      <nav ref={verticalNavRef} className={twMerge(verticalClasses, className)} aria-label="Section navigation">
        <div className="space-y-1" style={{ position: "sticky", top: stickyTop }}>
          {visibleSections.map((section) => {
            const isActive = activeSection === section.id;

            return (
              <a
                key={section.id}
                href={`#${sectionIdPrefix}${section.id}`}
                onClick={(e) => handleSectionClick(e, section.id)}
                className={twMerge(
                  "block w-full text-left px-3 py-2 rounded-md text-sm transition-colors no-underline",
                  isActive
                    ? "bg-background-muted text-text-body font-semibold"
                    : "text-text-muted hover:text-text-body hover:bg-background-hover"
                )}
                aria-current={isActive ? "page" : undefined}
              >
                <span className="flex items-center justify-between gap-2">
                  <span>{section.label}</span>
                  {section.count !== undefined && (
                    <span className="text-xs tabular-nums text-text-muted">{section.count}</span>
                  )}
                </span>
              </a>
            );
          })}
        </div>
      </nav>

      {/* Horizontal Tab Navigation */}
      <nav
        ref={horizontalNavRef}
        className={twMerge(horizontalClasses, "overflow-x-auto scrollbar-hide sticky top-0 z-10 py-4 -mt-4", className)}
        aria-label="Section navigation"
      >
        <div className="flex gap-2 min-w-max">
          {visibleSections.map((section) => {
            const isActive = activeSection === section.id;

            return (
              <a
                key={section.id}
                href={`#${sectionIdPrefix}${section.id}`}
                onClick={(e) => handleSectionClick(e, section.id)}
                className={twMerge(
                  "inline-block px-4 py-2 rounded-md text-sm whitespace-nowrap transition-colors no-underline",
                  isActive
                    ? "bg-background-muted text-text-body font-semibold"
                    : "text-text-muted hover:text-text-body hover:bg-background-hover"
                )}
                aria-current={isActive ? "page" : undefined}
              >
                <span className="flex items-center gap-2">
                  <span>{section.label}</span>
                  {section.count !== undefined && (
                    <span className="text-xs tabular-nums text-text-muted">({section.count})</span>
                  )}
                </span>
              </a>
            );
          })}
        </div>
      </nav>
    </>
  );
}
